import React, { useEffect, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, useForm } from 'react-hook-form'
import {
  CreateCompoundDto,
  CompoundDto,
  IsotopeDto,
  CompoundType,
  ProductType,
  ProductCatalogBaseDto,
} from '@services/dtos'
import { compoundSchema } from '@utils/schemas'
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import { Button, Dialog, DialogContent, FormControlLabel, Grid, Switch, Tooltip } from '@mui/material'
import { createCompound, updateCompound } from '@state/reducers/compoundReducer'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import { getIsotopes, setIsotopeFilters } from '@state/reducers/isotopeReducer'
import { UpTransition } from '../animations'
import IsotopeForm from './IsotopeForm'
import ServerAutocompleteMultiple from '../autocomplete/ServerAutocompleteMultiple'
import InputField from '../inputs/InputField'
import { FormCrudHeader } from './FormCrudHeader'
import SelectField from '../inputs/SelectField'
import { CompoundTypeLabels } from '@utils/constants'
import { AddWrapperInput } from '../inputs/AddWrapperInput'
import ProductCatalogForm from './productCatalog/ProductCatalogForm'
import ServerAutocomplete from '../autocomplete/ServerAutocomplete'
import { productCatalogService } from '@services/ProductCatalogService'

const COMPOUND_TYPE_OPTIONS = (
  Object.entries(CompoundTypeLabels) as Array<[CompoundType, string]>
).map(([value, label]) => ({ label, value }))

type Props = {
  editingCompound?: CompoundDto | null
  onClose: () => void
}

const CompoundForm: React.FC<Props> = ({ editingCompound, onClose }) => {
  const {
    handleSubmit,
    reset,
    control,
    watch,
    formState: { errors },
    setValue,
    clearErrors,
  } = useForm<CreateCompoundDto>({
    defaultValues: editingCompound
    ? {
        name: editingCompound.name,
        description: editingCompound.description,
        isotopeIds: editingCompound.isotopes.map(({ id }) => id) ?? [],
        type: editingCompound.type,
        coldKitId: editingCompound.coldKit?.id ?? null,
        eluateMaxAgeMinutes: editingCompound?.eluateMaxAgeMinutes ?? null,
        enforceEluateMaxAge: !!editingCompound?.eluateMaxAgeMinutes,
        preparationMaxAgeMinutes: editingCompound?.preparationMaxAgeMinutes ?? null,
        enforcePreparationMaxAge: !!editingCompound?.preparationMaxAgeMinutes,
      }
    : { isotopeIds: [], enforceEluateMaxAge: false, enforcePreparationMaxAge: false },
    resolver: yupResolver(compoundSchema),
  })

  const dispatch = useAppDispatch()
  const { filters } = useAppSelector(({ isotope }) => ({
    filters: isotope.filters,
  }))

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isIsotopeFormOpen, setIsotopeFormOpen] = useState(false)

  const cb = async (msg: string) => {
    dispatch(
      enqueueSnackbar({
        message: msg,
        options: { variant: 'success' },
      }),
    )
  }

  useEffect(() => {
    dispatch(getIsotopes())
    return () => {
      reset()
      dispatch(setIsotopeFilters({ ...filters, search: '' }))
    }
  }, [])

  useEffect(() => {
    if (editingCompound?.isotopes?.length) {
      for (const it of editingCompound.isotopes) {
        itemCacheRef.current.set(it.id, it)
      }
    }
  }, [editingCompound])

  const itemCacheRef = React.useRef(new Map<string, IsotopeDto>())

  const type = watch('type')

  useEffect(() => {
    switch (type) {
      case CompoundType.PURE_ISOTOPE:
        setValue('coldKitId', null)
        clearErrors('coldKitId')
        break
      case CompoundType.READY_TO_USE:
        setValue('coldKitId', null)
        setValue('isotopeIds', [])
        clearErrors(['coldKitId', 'isotopeIds'])
    }
  }, [type])

  const fetchIsotopes = async (search: string): Promise<IsotopeDto[]> => {
    dispatch(setIsotopeFilters({ search, page: 0 }))
    const page = await dispatch(getIsotopes()).unwrap()
    const list = page?.datas ?? []
    for (const it of list) itemCacheRef.current.set(it.id, it)
    return list
  }
  const fetchProductCatalogs = async (search: string) => {
    const page = await productCatalogService.getAll({ search, type: ProductType.KIT })
    return page?.datas ?? []
  }

  const onSubmitForm = async ({ enforceEluateMaxAge: _, enforcePreparationMaxAge: __, ...data }: CreateCompoundDto) => {
    setIsSubmitting(true)

    if (editingCompound) {
      dispatch(updateCompound({ id: editingCompound.id, dto: data }))
        .unwrap()
        .then(() => {
          cb('Le MRP a été modifié avec succès')
          onClose()
        })
        .catch(() =>
          cb(
            'Une erreur est survenue, vérifiez que le nom n\'est pas déjà utilisé',
          ),
        )
        .finally(() => setIsSubmitting(false))
    } else {
      dispatch(createCompound(data))
        .unwrap()
        .then(() => {
          cb('Le MRP a été créé avec succès')
          onClose()
        })
        .catch(() =>
          cb(
            'Une erreur est survenue, vérifiez que le nom n\'est pas déjà utilisé',
          ),
        )
        .finally(() => setIsSubmitting(false))
    }
  }

  return (
    <div className="flex w-full h-full p-4">
      <form
        onSubmit={(e) => {
          e.stopPropagation()
          handleSubmit(onSubmitForm)(e)
        }}
        className="w-full"
      >
        <FormCrudHeader
          isSubmitting={isSubmitting}
          onClose={onClose}
          editing={!!editingCompound}
        />

        <div className="max-w-screen-sm mx-auto mt-4">
          <fieldset className="border border-gray-200 p-4 rounded-lg">
            <legend className="text-lg font-bold mb-4 border border-gray-200 p-2 rounded-lg">
              MRP
            </legend>

            <Grid container rowSpacing={2} columnSpacing={4} mt={1}>
              <Grid item xs={12}>
                <InputField
                  control={control}
                  name="name"
                  label="Nom"
                  type="text"
                />
              </Grid>

              <Grid item xs={12}>
                <SelectField
                  control={control}
                  name="type"
                  options={COMPOUND_TYPE_OPTIONS}
                  value={type}
                  label="Type"
                />
              </Grid>

              {type === CompoundType.TO_PREPARE && (
                <Grid item xs={12}>
                  <AddWrapperInput
                    legend="Produit"
                    addTooltipTitle="Ajouter un produit dans le catalogue"
                    dialogContent={(close) => (
                      <ProductCatalogForm
                        onClose={close}
                        defaultValues={{ type: ProductType.KIT }}
                      />
                    )}
                    input={
                      <ServerAutocomplete<ProductCatalogBaseDto>
                        autoFetchOnMount
                        fetchService={fetchProductCatalogs}
                        onChange={(value: ProductCatalogBaseDto | null) => {
                          setValue('coldKitId', value?.id || null)
                        }}
                        getValue={(value) => value?.id?.toString() || null}
                        getOptionLabel={(value) => {
                          const product = value as ProductCatalogBaseDto
                          return product ? product.name : ''
                        }}
                        debounceMs={300}
                        placeholder="Rechercher un produit"
                        label="Trousse"
                        helperText={errors.coldKitId?.message}
                        error={!!errors.coldKitId}
                        minSearchLength={0}
                        initialOptions={
                          editingCompound?.coldKit
                            ? [editingCompound.coldKit]
                            : []
                        }
                        value={editingCompound?.coldKit ?? null}
                      />
                    }
                  />
                </Grid>
              )}

              {[CompoundType.PURE_ISOTOPE, CompoundType.TO_PREPARE].includes(
                type,
              ) && (
                <Grid item xs={12}>
                  <fieldset className="border border-gray-200 p-4 rounded-lg">
                    <legend className="text-sm font-medium text-gray-700 mb-1 border border-gray-200 p-2 rounded-lg">
                      Isotopes
                      <span className="inline-flex items-center">
                        <Tooltip title="Ajouter un isotope" arrow>
                          <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => setIsotopeFormOpen(true)}
                            sx={{
                              paddingTop: 0.5,
                              paddingBottom: 0.5,
                              paddingLeft: 1,
                              paddingRight: 1,
                              minWidth: 'auto',
                              width: 'auto',
                              marginLeft: 1,
                            }}
                          >
                            <i className="fas fa-plus"></i>
                          </Button>
                        </Tooltip>
                      </span>
                    </legend>

                    <Controller
                      name="isotopeIds"
                      control={control}
                      render={({ field: { value: ids = [], onChange } }) => {
                        const selected: IsotopeDto[] = ids
                          .map((id: string) => itemCacheRef.current.get(id))
                          .filter(Boolean) as IsotopeDto[]

                        return (
                          <ServerAutocompleteMultiple<IsotopeDto>
                            values={selected}
                            onChange={(isotopes) =>
                              onChange(isotopes.map(({ id }) => id))
                            }
                            fetchService={fetchIsotopes}
                            getOptionLabel={({ name, code }) => `${name} (${code})`}
                            label="Isotopes"
                            minSearchLength={1}
                            placeholder="Rechercher des isotopes..."
                            helperText={(errors?.isotopeIds as any)?.message}
                            error={!!errors?.isotopeIds}
                          />
                        )
                      }}
                    />
                  </fieldset>
                </Grid>
              )}

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!watch('enforceEluateMaxAge')}
                      onChange={(e) => {
                        setValue('enforceEluateMaxAge', e.target.checked)
                        if (!e.target.checked) setValue('eluateMaxAgeMinutes', null)
                      }}
                    />
                  }
                  label="Respecter fraîcheur éluat"
                />
              </Grid>

              {watch('enforceEluateMaxAge') && (
                <Grid item xs={12}>
                  <InputField
                    control={control}
                    name="eluateMaxAgeMinutes"
                    label="Durée max de l'éluat (minutes)"
                    type="number"
                    error={!!errors.eluateMaxAgeMinutes}
                    helperText={errors.eluateMaxAgeMinutes?.message}
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!watch('enforcePreparationMaxAge')}
                      onChange={(e) => {
                        setValue('enforcePreparationMaxAge', e.target.checked)
                        if (!e.target.checked) {
                          setValue('preparationMaxAgeMinutes', null)
                        }
                      }}
                    />
                  }
                  label="Péremption préparation"
                />
              </Grid>

              {watch('enforcePreparationMaxAge') && (
                <Grid item xs={12}>
                  <InputField
                    control={control}
                    name="preparationMaxAgeMinutes"
                    label="Âge max de préparation (minutes)"
                    type="number"
                    error={!!errors.preparationMaxAgeMinutes}
                    helperText={errors.preparationMaxAgeMinutes?.message}
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <InputField
                  control={control}
                  name={'description'}
                  label={'Description'}
                  type="text"
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          </fieldset>
        </div>
      </form>

      <Dialog
        open={isIsotopeFormOpen}
        onClose={() => setIsotopeFormOpen(false)}
        TransitionComponent={UpTransition}
        slotProps={{
          backdrop: {},
        }}
      >
        <DialogContent>
          {isIsotopeFormOpen && (
            <IsotopeForm
              onClose={() => {
                setIsotopeFormOpen(false)
                dispatch(getIsotopes())
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default CompoundForm
